import {useMemo} from 'react';

import {useRampFlagUseBackendSmoothing} from '../../../util/rampFeatureFlags/linePlotFeatureFlags';
import {DEFAULT_SMOOTHING_SETTINGS} from '../../WorkspaceDrawer/Settings/runLinePlots/linePlotDefaults';
import {getSmoothingTypeDisplayName} from '../../WorkspaceDrawer/Settings/runLinePlots/LinePlotSmoothing';
import {type LinePlotDisplayFnArgs} from '../types';
import {createNumberDisplayFn} from './linePlotDisplayFactories';

/**
 * Hook that returns the display row for smoothingType setting.
 * Uses the backend smoothing feature flag to determine display name.
 * This is a hook (not using factory) because it needs to call useRampFlagUseBackendSmoothing.
 *
 * @example
 * const smoothingTypeRow = useSmoothingTypeDisplayRow({
 *   currTemplateLinePlotSettings,
 *   currWorkspaceLinePlotSettings,
 * });
 */
export const useSmoothingTypeDisplayRow = ({
  currTemplateLinePlotSettings,
  currWorkspaceLinePlotSettings,
}: Omit<LinePlotDisplayFnArgs, 'isBackendSmoothingEnabled'>) => {
  const isBackendSmoothingEnabled = useRampFlagUseBackendSmoothing();

  return useMemo(() => {
    const currValue = currTemplateLinePlotSettings?.smoothingType;
    const newValue =
      currWorkspaceLinePlotSettings?.smoothingType ??
      DEFAULT_SMOOTHING_SETTINGS.smoothingType;

    return {
      groupTitle: 'Smoothing',
      label: 'Type',
      field: 'smoothingType' as const,
      currValue,
      currDisplayValue:
        currValue == null
          ? undefined
          : getSmoothingTypeDisplayName(currValue, isBackendSmoothingEnabled),
      newValue,
      newDisplayValue: getSmoothingTypeDisplayName(
        newValue,
        isBackendSmoothingEnabled
      ),
    };
  }, [
    currTemplateLinePlotSettings,
    currWorkspaceLinePlotSettings,
    isBackendSmoothingEnabled,
  ]);
};

export const getSmoothingWeightDisplayRow = createNumberDisplayFn({
  field: 'smoothingWeight',
  label: 'Weight',
  groupTitle: 'Smoothing',
  defaultValue: DEFAULT_SMOOTHING_SETTINGS.smoothingWeight,
});
